"use client"

import { useState, useEffect } from "react"
import { Modal } from "../ui/modal"
import Button from "../ui/button/Button"
import Input from "../form/input/InputField"
import Badge from "../ui/badge/Badge"
import useApi from "../../hooks/useApi"
import { toast } from "react-hot-toast"
import RoleBasedRenderer from "../auth/RoleBasedRenderer"
import { 
  Search, 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Building2, 
  Map, 
  DollarSign,
  ChevronRight,
  Users
} from "lucide-react"

interface CustomPriceModalProps {
  isOpen: boolean
  onClose: () => void
}

// Define interfaces locally instead of importing them
interface AgenciaSimple {
  id: number
  name: string
  email: string
  phone: string
}

interface Excursion {
  id: number
  name: string
  description: string
  priceB2CAdult: number
  priceB2CChild?: number | null
  priceB2BAdult: number
  priceB2BChild?: number | null
  maxCapacity: number
  booked: number
  active: boolean
  destination: string
  isThirdParty: boolean
}

interface AgencyPrice {
  id: number
  agencyId: number
  excursionId: number
  priceAdult: number
  priceChild: number | null
  agency?: {
    name: string
  }
  excursion?: {
    name: string
  }
}

interface PricedExcursion extends Excursion {
  hasCustomPrice: boolean
  customPriceId: number | null
  customPriceAdult: number | null
  customPriceChild: number | null
  regularPriceAdult: number
  regularPriceChild: number | null
}

type ModalStep = 'agency' | 'excursion' | 'price';

export default function CustomPriceModal({ isOpen, onClose }: CustomPriceModalProps) {
  const { 
    getAgencias, 
    getAgencyCustomPrices, 
    getAgencyPricedExcursions, 
    getAllExcursionsWithAgencyPrices, 
    createCustomPrice, 
    updateCustomPrice, 
    deleteCustomPrice 
  } = useApi()
  
  const [step, setStep] = useState<ModalStep>("agency")
  const [agencies, setAgencies] = useState<AgenciaSimple[]>([])
  const [filteredAgencies, setFilteredAgencies] = useState<AgenciaSimple[]>([])
  const [selectedAgency, setSelectedAgency] = useState<AgenciaSimple | null>(null)
  const [excursions, setExcursions] = useState<PricedExcursion[]>([])
  const [filteredExcursions, setFilteredExcursions] = useState<PricedExcursion[]>([])
  const [selectedExcursion, setSelectedExcursion] = useState<PricedExcursion | null>(null)
  const [agencySearch, setAgencySearch] = useState("")
  const [excursionSearch, setExcursionSearch] = useState("")
  const [priceAdult, setPriceAdult] = useState("")
  const [priceChild, setPriceChild] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentPriceId, setCurrentPriceId] = useState<number | null>(null)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  // Load agencies when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAgencies()
    } else {
      // Reset states when modal closes
      setStep("agency")
      setSelectedAgency(null)
      setSelectedExcursion(null)
      setPriceAdult("")
      setPriceChild("")
      setAgencySearch("")
      setExcursionSearch("")
      setCurrentPriceId(null)
    }
  }, [isOpen])

  // Filter agencies based on search
  useEffect(() => {
    if (agencySearch.trim() === "") {
      setFilteredAgencies(agencies)
    } else {
      const filtered = agencies.filter(agency => 
        agency.name.toLowerCase().includes(agencySearch.toLowerCase()) ||
        agency.email.toLowerCase().includes(agencySearch.toLowerCase())
      )
      setFilteredAgencies(filtered)
    }
  }, [agencies, agencySearch])

  // Filter excursions based on search
  useEffect(() => {
    if (!excursions || excursions.length === 0) {
      setFilteredExcursions([])
      return
    }
    
    if (excursionSearch.trim() === "") {
      setFilteredExcursions(excursions)
    } else {
      const searchTerm = excursionSearch.toLowerCase().trim()
      const filtered = excursions.filter(excursion => 
        (excursion.name && excursion.name.toLowerCase().includes(searchTerm)) ||
        (excursion.description && excursion.description.toLowerCase().includes(searchTerm)) ||
        (excursion.destination && excursion.destination.toLowerCase().includes(searchTerm))
      )
      setFilteredExcursions(filtered)
    }
  }, [excursions, excursionSearch])

  // Load initial data
  const loadAgencies = async () => {
    try {
      setIsLoading(true)
      const data = await getAgencias()
      setAgencies(data)
      setFilteredAgencies(data)
    } catch (error) {
      console.error("Error loading agencies:", error)
      toast.error("Error al cargar las agencias")
    } finally {
      setIsLoading(false)
    }
  }

  const loadExcursions = async (agencyId: number) => {
    try {
      setIsLoading(true)
      // Use the new function that gets all excursions with agency price information
      const data = await getAllExcursionsWithAgencyPrices(agencyId)
      
      if (!data || data.length === 0) {
        toast.error("No hay excursiones disponibles")
      }
      
      setExcursions(data || [])
      setFilteredExcursions(data || [])
    } catch (error) {
      console.error("Error loading excursions:", error)
      toast.error("Error al cargar las excursiones")
      // Initialize with empty arrays to prevent undefined errors
      setExcursions([])
      setFilteredExcursions([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleAgencySelect = async (agency: AgenciaSimple) => {
    setSelectedAgency(agency)
    setExcursionSearch("") // Clear any existing search term
    await loadExcursions(agency.id)
    setStep("excursion")
  }

  const handleExcursionSelect = (excursion: PricedExcursion) => {
    setSelectedExcursion(excursion)
    
    // If there's already a custom price, pre-fill the form
    if (excursion.hasCustomPrice) {
      setPriceAdult(excursion.customPriceAdult?.toString() || "")
      setPriceChild(excursion.customPriceChild?.toString() || "")
      setCurrentPriceId(excursion.customPriceId)
    } else {
      // Otherwise, pre-fill with regular B2B prices
      const adultPrice = excursion.regularPriceAdult || excursion.priceB2BAdult
      const childPrice = excursion.regularPriceChild || excursion.priceB2BChild
      
      setPriceAdult(adultPrice?.toString() || "")
      setPriceChild(childPrice?.toString() || "")
      setCurrentPriceId(null)
    }
    
    setStep("price")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedAgency || !selectedExcursion) {
      toast.error("Por favor seleccione una agencia y excursión")
      return
    }
    
    if (!priceAdult || parseFloat(priceAdult) <= 0) {
      toast.error("El precio para adultos es requerido y debe ser mayor a 0")
      return
    }
    
    setIsSubmitting(true)
    
    try {
      const priceData = {
        agencyId: selectedAgency.id,
        excursionId: selectedExcursion.id,
        priceAdult: parseFloat(priceAdult),
        priceChild: priceChild ? parseFloat(priceChild) : null
      }
      
      if (currentPriceId) {
        // Update existing price
        const { priceAdult, priceChild } = priceData
        await updateCustomPrice(currentPriceId, { priceAdult, priceChild })
        toast.success("Precio personalizado actualizado correctamente")
      } else {
        // Create new price
        await createCustomPrice(priceData)
        toast.success("Precio personalizado creado correctamente")
      }
      
      // Reload excursions to reflect the changes
      await loadExcursions(selectedAgency.id)
      
      // Go back to excursion list
      setStep("excursion")
    } catch (error) {
      console.error("Error updating custom price:", error)
      toast.error("Error al guardar el precio personalizado")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeletePrice = async () => {
    if (!currentPriceId || !selectedAgency) {
      return
    }
    
    setIsSubmitting(true)
    
    try {
      await deleteCustomPrice(currentPriceId)
      toast.success("Precio personalizado eliminado correctamente")
      
      // Reload excursions and go back to excursion list
      await loadExcursions(selectedAgency.id)
      setStep("excursion")
      setIsDeleteConfirmOpen(false)
    } catch (error) {
      console.error("Error deleting custom price:", error)
      toast.error("Error al eliminar el precio personalizado")
    } finally {
      setIsSubmitting(false)
    }
  }

  const goToStep = (newStep: ModalStep) => {
    // Only allow navigation to steps that are accessible based on current state
    if (newStep === "agency") {
      setStep("agency")
    } else if (newStep === "excursion" && selectedAgency) {
      setStep("excursion")
    } else if (newStep === "price" && selectedAgency && selectedExcursion) {
      setStep("price")
    }
  }

  const getStepIcon = (stepName: ModalStep) => {
    switch (stepName) {
      case "agency":
        return <Building2 className="h-5 w-5" />
      case "excursion":
        return <Map className="h-5 w-5" />
      case "price":
        return <DollarSign className="h-5 w-5" />
    }
  }

  const renderAgenciesContent = () => (
    <div className="space-y-5">
      <div className="relative">
        <Input
          type="text"
          placeholder="Buscar agencias..."
          value={agencySearch}
          onChange={(e) => setAgencySearch(e.target.value)}
          className="w-full pl-10 focus:ring-primary-500 dark:bg-gray-800/80 dark:border-gray-700 dark:text-white dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-500"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-3 text-gray-600 dark:text-gray-300 font-medium">Cargando agencias...</span>
        </div>
      ) : filteredAgencies.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 dark:bg-gray-800/40 rounded-lg border border-gray-200 dark:border-gray-700">
          <XCircle className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-gray-700 dark:text-gray-200 text-lg font-medium">No se encontraron agencias</p>
          <p className="mt-1 text-gray-500 dark:text-gray-400">Intenta con otros términos de búsqueda</p>
        </div>
      ) : (
        <div className="grid gap-3 max-h-[calc(100vh-220px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
          {filteredAgencies.map((agency) => (
            <div
              key={agency.id}
              onClick={() => handleAgencySelect(agency)}
              className="p-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex justify-between items-center shadow-sm hover:shadow-md dark:shadow-gray-900/10 hover:border-gray-300 dark:hover:border-gray-600"
            >
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white text-lg">{agency.name}</h3>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{agency.email}</span>
                  {agency.phone && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">• {agency.phone}</span>
                  )}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const renderExcursionsContent = () => (
    <div className="space-y-5">
      <div className="relative">
        <Input
          type="text"
          placeholder="Buscar excursiones..."
          value={excursionSearch}
          onChange={(e) => setExcursionSearch(e.target.value)}
          className="w-full pl-10 focus:ring-primary-500 dark:bg-gray-800/80 dark:border-gray-700 dark:text-white dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-500"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
      </div>
      
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500 mb-3" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">Cargando excursiones...</p>
        </div>
      ) : !excursions || excursions.length === 0 ? (
        <div className="py-10 text-center bg-gray-50 dark:bg-gray-800/40 rounded-lg border border-gray-200 dark:border-gray-700">
          <XCircle className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-gray-700 dark:text-gray-200 text-lg font-medium">No hay excursiones disponibles</p>
          <p className="text-gray-500 dark:text-gray-400 mt-1.5">
            No se encontraron excursiones activas en el sistema
          </p>
        </div>
      ) : filteredExcursions.length === 0 ? (
        <div className="py-10 text-center bg-gray-50 dark:bg-gray-800/40 rounded-lg border border-gray-200 dark:border-gray-700">
          <Search className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-gray-700 dark:text-gray-200 text-lg font-medium">No se encontraron resultados</p>
          <p className="text-gray-500 dark:text-gray-400 mt-1.5">
            Intenta con otros términos de búsqueda
          </p>
        </div>
      ) : (
        <div className="grid gap-4 max-h-[calc(100vh-220px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
          {filteredExcursions.map((excursion) => (
            <div
              key={excursion.id}
              onClick={() => handleExcursionSelect(excursion)}
              className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm hover:shadow-md dark:shadow-gray-900/10 overflow-hidden hover:border-gray-300 dark:hover:border-gray-600"
            >
              <div className="p-5">
                <div className="flex justify-between items-start">
                  <h3 className="font-medium text-gray-900 dark:text-white text-lg">{excursion.name}</h3>
                  {excursion.hasCustomPrice && (
                    <div className="flex-shrink-0 ml-2">
                      <Badge variant="light" color="primary" startIcon={<CheckCircle className="h-3 w-3" />}>
                        Precio personalizado
                      </Badge>
                    </div>
                  )}
                </div>
                
                {excursion.destination && (
                  <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex items-center">
                    <Map className="h-3.5 w-3.5 mr-1.5" />
                    {excursion.destination}
                  </div>
                )}
                
                {excursion.description && (
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                    {excursion.description}
                  </p>
                )}
              </div>
              
              <div className="px-5 py-4 bg-gray-50 dark:bg-gray-800/90 border-t border-gray-200 dark:border-gray-700 grid grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Precio regular</span>
                  <div className="flex flex-col md:flex-row md:items-center md:space-x-3 mt-1.5">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Adulto: <span className="font-semibold">${excursion.regularPriceAdult || excursion.priceB2BAdult}</span>
                    </span>
                    {(excursion.regularPriceChild != null || excursion.priceB2BChild != null) && (
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Niño: <span className="font-semibold">${excursion.regularPriceChild || excursion.priceB2BChild}</span>
                      </span>
                    )}
                  </div>
                </div>
                
                {excursion.hasCustomPrice && (
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Precio personalizado</span>
                    <div className="flex flex-col md:flex-row md:items-center md:space-x-3 mt-1.5">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Adulto: <span className="font-semibold text-primary-600 dark:text-primary-400">${excursion.customPriceAdult}</span>
                      </span>
                      {excursion.customPriceChild != null && (
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Niño: <span className="font-semibold text-primary-600 dark:text-primary-400">${excursion.customPriceChild}</span>
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const renderPriceContent = () => (
    <form onSubmit={handleSubmit} className="space-y-7">
      <div className="bg-gray-50 dark:bg-gray-800/70 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedExcursion?.name}
            </h3>
            <div className="flex items-center gap-2 mt-2">
              <Users className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Capacidad: {selectedExcursion?.maxCapacity || "N/A"}
              </span>
              
              {selectedExcursion?.destination && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <Map className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {selectedExcursion.destination}
                  </span>
                </>
              )}
            </div>
          </div>
          
          <div className="self-start mt-2 md:mt-0">
            <Badge 
              variant="light" 
              color={selectedExcursion?.hasCustomPrice ? "primary" : "light"}
              startIcon={selectedExcursion?.hasCustomPrice ? <CheckCircle className="h-3 w-3" /> : undefined}
            >
              {selectedExcursion?.hasCustomPrice ? "Precio personalizado" : "Precio regular"}
            </Badge>
          </div>
        </div>
      </div>
      
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Precio personalizado (Adulto) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
              <Input
                type="number"
                value={priceAdult}
                onChange={(e) => setPriceAdult(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full pl-8 focus:ring-primary-500 dark:bg-gray-800/80 dark:border-gray-700 dark:text-white dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-500"
              />
            </div>
            {selectedExcursion && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1.5 bg-gray-50 dark:bg-gray-700/60 p-2.5 rounded border border-gray-200 dark:border-gray-700">
                <span>Precio regular:</span>
                <span className="font-medium">${selectedExcursion.regularPriceAdult || selectedExcursion.priceB2BAdult}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Precio Personalizado (Niño)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
              <Input
                type="number"
                value={priceChild}
                onChange={(e) => setPriceChild(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full pl-8 focus:ring-primary-500 dark:bg-gray-800/80 dark:border-gray-700 dark:text-white dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-500"
              />
            </div>
            {selectedExcursion && (selectedExcursion.regularPriceChild != null || selectedExcursion.priceB2BChild != null) && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1.5 bg-gray-50 dark:bg-gray-700/60 p-2.5 rounded border border-gray-200 dark:border-gray-700">
                <span>Precio regular:</span>
                <span className="font-medium">${selectedExcursion.regularPriceChild || selectedExcursion.priceB2BChild}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row justify-end gap-3 pt-5 border-t border-gray-200 dark:border-gray-700 mt-6">
        {currentPriceId && (
          <RoleBasedRenderer requiredPermission="delete">
            <Button
              variant="primary"
              type="button"
              disabled={isSubmitting}
              onClick={() => setIsDeleteConfirmOpen(true)}
              className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white order-2 sm:order-1"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Eliminar precio
            </Button>
          </RoleBasedRenderer>
        )}
        <RoleBasedRenderer requiredPermission="edit">
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting}
            className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white order-3"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {currentPriceId ? 'Actualizando...' : 'Guardando...'}
              </>
            ) : (
              currentPriceId ? 'Actualizar precio' : 'Guardar precio'
            )}
          </Button>
        </RoleBasedRenderer>
      </div>
    </form>
  )

  const renderContent = () => {
    switch (step) {
      case "agency":
        return renderAgenciesContent()
      case "excursion":
        return renderExcursionsContent()
      case "price":
        return renderPriceContent()
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Precios personalizados"
        size="xl"
      >
        <div className="flex flex-col md:flex-row h-full">
          {/* Sidebar Navigation - Improved for mobile */}
          <div className="w-full md:w-72 pr-0 md:pr-0 pb-3 md:pb-0 mb-4 md:mb-0 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700">
            <div className="flex flex-row md:flex-col gap-2 overflow-x-auto md:overflow-visible py-1 px-1 md:px-0 md:pr-4 md:pl-1 no-scrollbar">
              <div 
                onClick={() => goToStep("agency")}
                className={`flex items-center p-2.5 md:p-3.5 rounded-lg cursor-pointer transition-all flex-shrink-0 min-w-[110px] md:min-w-0 ${
                  step === "agency" 
                    ? "bg-primary-50 dark:bg-primary-600/20 text-primary-700 dark:text-primary-200 shadow-sm dark:ring-1 dark:ring-primary-400/50 border border-transparent dark:border-primary-500/40 dark:backdrop-blur-sm" 
                    : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 border border-transparent"
                }`}
              >
                <Building2 className={`h-4 w-4 md:h-5 md:w-5 mr-2 md:mr-3 ${
                  step === "agency"
                    ? "text-gray-500 dark:text-gray-400"
                    : "text-gray-500 dark:text-gray-400" 
                }`} />
                <div>
                  <p className="font-medium text-sm md:text-base text-gray-500 dark:text-gray-400">Agencias</p>
                  <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 hidden md:block">Seleccionar agencia</p>
                </div>
              </div>
              
              <div 
                onClick={() => selectedAgency && goToStep("excursion")}
                className={`flex items-center p-2.5 md:p-3.5 rounded-lg transition-all flex-shrink-0 min-w-[110px] md:min-w-0 ${
                  selectedAgency
                    ? `cursor-pointer ${
                        step === "excursion" 
                          ? "bg-primary-50 dark:bg-primary-600/20 text-primary-700 dark:text-primary-200 shadow-sm dark:ring-1 dark:ring-primary-400/50 border border-transparent dark:border-primary-500/40 dark:backdrop-blur-sm" 
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 border border-transparent"
                      }`
                    : "opacity-50 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed text-gray-500 dark:text-gray-500 border border-transparent"
                }`}
              >
                <Map className={`h-4 w-4 md:h-5 md:w-5 mr-2 md:mr-3 ${
                  step === "excursion" && selectedAgency
                    ? "text-gray-500 dark:text-gray-400"
                    : "text-gray-500 dark:text-gray-400" 
                }`} />
                <div>
                  <p className="font-medium text-sm md:text-base text-gray-500 dark:text-gray-400">Excursiones</p>
                  <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 hidden md:block">Seleccionar excursión</p>
                </div>
              </div>
              
              <div 
                onClick={() => selectedAgency && selectedExcursion && goToStep("price")}
                className={`flex items-center p-2.5 md:p-3.5 rounded-lg transition-all flex-shrink-0 min-w-[110px] md:min-w-0 ${
                  selectedAgency && selectedExcursion
                    ? `cursor-pointer ${
                        step === "price" 
                          ? "bg-primary-50 dark:bg-primary-600/20 text-primary-700 dark:text-primary-200 shadow-sm dark:ring-1 dark:ring-primary-400/50 border border-transparent dark:border-primary-500/40 dark:backdrop-blur-sm" 
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 border border-transparent"
                      }`
                    : "opacity-50 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed text-gray-500 dark:text-gray-500 border border-transparent"
                }`}
              >
                <DollarSign className={`h-4 w-4 md:h-5 md:w-5 mr-2 md:mr-3 ${
                  step === "price" && selectedAgency && selectedExcursion
                    ? "text-gray-500 dark:text-gray-400"
                    : "text-gray-500 dark:text-gray-400" 
                }`} />
                <div>
                  <p className="font-medium text-sm md:text-base text-gray-500 dark:text-gray-400">Precios</p>
                  <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 hidden md:block">Configurar precios</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 px-1 md:px-6 md:pl-8 md:border-l border-transparent dark:border-transparent">
            {/* Breadcrumb Navigation - Mobile Only */}
            <div className="flex items-center mb-4 md:hidden">
              {step !== "agency" && (
                <button 
                  onClick={() => goToStep(step === "price" ? "excursion" : "agency")}
                  className="mr-2 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </button>
              )}
              
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                <span className={step === "agency" ? "font-medium text-primary-600 dark:text-primary-400" : ""}>
                  Agencias
                </span>
                
                {(step === "excursion" || step === "price") && (
                  <>
                    <ChevronRight className="h-4 w-4 mx-1" />
                    <span className={step === "excursion" ? "font-medium text-primary-600 dark:text-primary-400" : ""}>
                      Excursiones
                    </span>
                  </>
                )}
                
                {step === "price" && (
                  <>
                    <ChevronRight className="h-4 w-4 mx-1" />
                    <span className="font-medium text-primary-600 dark:text-primary-400">
                      Precios
                    </span>
                  </>
                )}
              </div>
            </div>
            
            {/* Step Content */}
            {renderContent()}
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal for Deletion */}
      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title="Confirmar eliminación"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            ¿Está seguro que desea eliminar este precio personalizado? Esta acción no se puede deshacer.
          </p>
          
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="border-gray-300 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleDeletePrice}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : 'Eliminar precio'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
} 